import React, { Suspense, useEffect, useMemo, useState } from "react";
import LoadingSpinner from "../../components/UI/loadingSpinner/LoadingSpinner";
import { ROUTES } from "../../constants/routes";
import { downloadCSV } from "../../utils/csv";
import {
  PAGINATION_SEQUENCE,
  workDetailReportHeaderMapping,
  workDetailReportHeaders,
} from "../../constants";
import ExportIcon from "../../components/icons/ExportIcon";
import { Icon } from "@iconify/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import { getApi } from "../../services/http.service";
import { Link } from "react-router-dom";
import { STATES_OPTIONS } from "../../constants/states";
import DateRangeDropdown from "../../components/UI/date-range-dropdown/DateRangeDropdown";
import Dropdown from "../../components/UI/dropdown/Dropdown";
import { IDaySale } from "../../interfaces/daysales.interface";
import { IEmployee } from "../../interfaces/employee.interface";
import { totalJuiceCases, totalWaterCases } from "../../utils";
import { WORK_DETAILS_COLUMNS } from "./columns";

function WorkDetails() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const params = useParams();
  const [queryParams] = useSearchParams();
  // Data & Search
  const [state, setState] = useState("");
  const [startDate, setStartDate] = useState(queryParams.get("startDate"));
  const [endDate, setEndDate] = useState(queryParams.get("endDate"));
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState<IDaySale[]>([]);
  const [employeeData, setEmployeeData] = useState<IEmployee>();
  const [show, setShow] = useState(false);
  useEffect(() => {
    fetchNews(page, perPage, search);
  }, [page, perPage, search, state, startDate, endDate]);

  const fetchNews = async (page: number, limit: number, search: string) => {
    try {
      setLoading(true);
      const result = await getApi(ROUTES.GET_ALL_DAY_SALES, {
        page,
        limit,
        search,
        startDate,
        endDate,
        employee: params?.id,
        state,
      });
      if (result.code === 200) {
        setFilteredData(result?.data);
        setTotalRows(result.total); // Set total records
        setEmployeeData(result?.employee);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Page Change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle Rows per Page Change
  const handlePerRowsChange = (newPerPage: number, newPage: number) => {
    setPerPage(newPerPage);
    setPage(newPage);
  };

  const columns: TableColumn<IDaySale>[] = useMemo(
    () => WORK_DETAILS_COLUMNS,
    [filteredData?.length]
  );

  async function selectedChangeHandler(e: any) {
    const s = e[0].toString();
    const en = e[1]?.toString();
    if (s && en) {
      setStartDate(s);
      setEndDate(en);
    }
  }

  function stateChangeHandler(e: React.ChangeEvent<HTMLSelectElement>) {
    setState(() => e.target.value);
  }
  return (
    <section>
      <div className="d-flex justify-content-between flex-wrap align-items-center">
        <div className="d-flex align-items-center">
          <button className="nav-link" onClick={() => navigate(-1)}>
            <Icon icon="material-symbols:arrow-back-rounded" fontSize={25} />
          </button>
          <h2 className="title mb-1"> Working Details</h2>
        </div>
        <div className="d-flex flex-wrap align-items-center">
          <Dropdown
            customClass="mb-0"
            dropdownData={STATES_OPTIONS}
            bindLabel="state"
            bindValue="state"
            selected={state}
            label="State"
            onChange={stateChangeHandler}
          />
          <DateRangeDropdown onChange={selectedChangeHandler} />
          <span className="ms-2">
            <button
              className="btn btn-primary"
              onClick={() => {
                const data = filteredData.map((r: any) => {
                  r.orderJuice = totalJuiceCases(r?.sales);
                  r.orderWater = totalWaterCases(r?.sales);
                  r.outletVisit = r.sales?.length;
                  return r;
                });
                downloadCSV(
                  data,
                  workDetailReportHeaders,
                  workDetailReportHeaderMapping,
                  "Groove_Work"
                );
              }}
            >
              <ExportIcon />
              <span className="ps-1"> Export</span>
            </button>
          </span>
        </div>
      </div>
      <div className="position-relative">
        <h6>Employee: {employeeData?.fullName}</h6>
        <div className="fs-7 mb-1">
          <div>
            <span>Email: {employeeData?.email}</span>
          </div>
          <div>
            <span>Phone: {employeeData?.phone}</span>
          </div>
          <div>
            <span>
              Address: {employeeData?.address?.street}{" "}
              {employeeData?.address?.city} {employeeData?.address?.state}{" "}
              {employeeData?.address?.country} -{" "}
              {employeeData?.address?.pincode}
            </span>
          </div>
        </div>
        <div className="position-absolute top-0 end-0">
          <Link to={`/employees/${employeeData?._id}`} title="View Employee">
            <Icon
              icon="material-symbols:frame-person-sharp"
              type="button"
              color="#9bcb51"
              style={{ marginInline: 5 }}
              fontSize={25}
            />
          </Link>
        </div>
      </div>
      <Suspense>
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={PAGINATION_SEQUENCE}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          highlightOnHover
          striped
          progressPending={loading}
          progressComponent={<LoadingSpinner />}
        />
      </Suspense>
    </section>
  );
}

export default WorkDetails;
